import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Volume2, VolumeX } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import CinemaAudio from '../lib/cinemaAudio';
import { INTRO_WORDS } from '../mock/introPanels';

const WORD_DURATION = 520; // ms each word is on-screen
const NAME_PHASE_MS = 3400;

const Word = ({ word, isFinal }) => (
  <motion.div
    key={word}
    initial={{
      opacity: 0,
      filter: 'blur(22px)',
      scale: 1.18,
      letterSpacing: '0.5em',
    }}
    animate={{
      opacity: 1,
      filter: 'blur(0px)',
      scale: 1,
      letterSpacing: '0.01em',
    }}
    exit={{
      opacity: 0,
      filter: 'blur(14px)',
      scale: 0.94,
      letterSpacing: '0.12em',
    }}
    transition={{ duration: WORD_DURATION / 1000, ease: [0.22, 1, 0.36, 1] }}
    className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-6"
  >
    <div
      className="text-[10px] tracking-[0.5em] uppercase mb-4"
      style={{ color: 'rgba(196,163,90,0.55)' }}
    >
      Attribute
    </div>
    <div
      className={isFinal ? 'metallic-gold' : 'metallic-gold'}
      style={{
        fontFamily: "'Playfair Display', serif",
        fontWeight: 900,
        fontSize: isFinal ? 'clamp(4.5rem, 14vw, 13rem)' : 'clamp(3.2rem, 10vw, 9.5rem)',
        lineHeight: 1,
        WebkitTextStroke: '1px rgba(0,0,0,0.75)',
        letterSpacing: '-0.01em',
        textShadow: '0 6px 0 rgba(0,0,0,0.7), 0 14px 40px rgba(0,0,0,0.85)',
      }}
    >
      {word}
    </div>
    <div
      className="mt-5 h-[1px]"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(196,163,90,0.85) 25%, #f6e7b3 50%, rgba(196,163,90,0.85) 75%, transparent)',
        width: isFinal ? '320px' : '240px',
        boxShadow: '0 2px 8px rgba(196,163,90,0.2)',
      }}
    />
  </motion.div>
);

const IntroSequence = ({ onFinish }) => {
  const { t } = useLanguage();
  const [phase, setPhase] = useState('gate'); // 'gate' | 'words' | 'name' | 'done'
  const [wordIdx, setWordIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  // Lock scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = phase === 'done' ? prev : 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [phase]);

  // Advance through words
  useEffect(() => {
    if (phase !== 'words') return;
    if (wordIdx >= INTRO_WORDS.length) {
      setPhase('name');
      return;
    }
    const tm = setTimeout(() => setWordIdx((i) => i + 1), WORD_DURATION);
    return () => clearTimeout(tm);
  }, [wordIdx, phase]);

  // Hold on name phase
  useEffect(() => {
    if (phase !== 'name') return;
    const tm = setTimeout(() => setPhase('done'), NAME_PHASE_MS);
    return () => clearTimeout(tm);
  }, [phase]);

  // Fade out / notify parent
  useEffect(() => {
    if (phase !== 'done') return;
    if (audioRef.current) {
      audioRef.current.stop(800);
      audioRef.current = null;
    }
    const tm = setTimeout(() => onFinish?.(), 800);
    return () => clearTimeout(tm);
  }, [phase, onFinish]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.stop(200);
        audioRef.current = null;
      }
    };
  }, []);

  const handleBegin = () => {
    if (!muted) {
      // Panel beats align with each word reveal (for bell chimes)
      const panels = INTRO_WORDS.map((_, i) => ({ tMs: i * WORD_DURATION }));
      const finalHitMs = INTRO_WORDS.length * WORD_DURATION + 50;
      const totalMs = finalHitMs + NAME_PHASE_MS + 400;
      const audio = new CinemaAudio();
      audio.start({ panels, finalHitMs, totalMs });
      audioRef.current = audio;
    }
    setPhase('words');
  };

  const handleSkip = () => setPhase('done');

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          className="fixed inset-0 z-[100] overflow-hidden"
          style={{ background: '#000' }}
        >
          {/* Subtle gold radial glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at 50% 45%, rgba(196,163,90,0.10) 0%, rgba(0,0,0,0.0) 60%)',
              zIndex: 1,
            }}
          />

          {/* Fine white halftone dot overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1.5px)',
              backgroundSize: '4px 4px',
              opacity: 0.3,
              zIndex: 2,
            }}
          />

          {/* Warm gold glow on final chord */}
          <AnimatePresence>
            {phase === 'name' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.55 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9 }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse at 50% 50%, rgba(246,231,179,0.35) 0%, rgba(196,163,90,0.15) 40%, rgba(0,0,0,0) 80%)',
                  zIndex: 3,
                  mixBlendMode: 'screen',
                }}
              />
            )}
          </AnimatePresence>

          {/* GATE */}
          {phase === 'gate' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              className="absolute inset-0 flex items-center justify-center z-40 px-6"
            >
              <div className="text-center max-w-xl">
                <div className="label-small mb-5" style={{ color: '#C4A35A' }}>
                  {t.intro.credential}
                </div>
                <div className="gold-rule mx-auto mb-8" />
                <h2
                  className="font-heading text-4xl md:text-5xl lg:text-6xl leading-tight"
                  style={{ color: '#F0EDE8' }}
                >
                  Kenneth Christopher<br />
                  <span className="italic" style={{ color: '#C4A35A' }}>
                    &ldquo;KC&rdquo; Redding
                  </span>
                </h2>
                <p
                  className="font-heading italic mt-6 text-lg md:text-xl"
                  style={{ color: '#DCCFAD' }}
                >
                  {t.intro.tagline}
                </p>

                <div className="mt-12 flex flex-col items-center gap-4">
                  <motion.button
                    onClick={handleBegin}
                    data-testid="intro-enter-button"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn-gold"
                    style={{ padding: '1.1rem 2.4rem' }}
                  >
                    <Play size={14} fill="#0A0E1A" />
                    {t.intro.enter}
                  </motion.button>

                  <button
                    onClick={() => setMuted((m) => !m)}
                    data-testid="intro-sound-toggle"
                    className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase mt-2 transition-colors"
                    style={{ color: muted ? '#8a7a54' : '#C4A35A' }}
                  >
                    {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    {muted ? t.intro.soundOff : t.intro.soundOn}
                  </button>

                  <div
                    className="text-[10px] tracking-[0.3em] uppercase mt-1"
                    style={{ color: '#4f4530' }}
                  >
                    {t.intro.soundHint}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* WORDS */}
          {phase === 'words' && (
            <AnimatePresence mode="wait">
              {wordIdx < INTRO_WORDS.length && (
                <Word
                  key={INTRO_WORDS[wordIdx]}
                  word={INTRO_WORDS[wordIdx]}
                  isFinal={wordIdx === INTRO_WORDS.length - 1}
                />
              )}
            </AnimatePresence>
          )}

          {/* NAME REVEAL */}
          <AnimatePresence>
            {phase === 'name' && (
              <motion.div
                key="final-name"
                initial={{ opacity: 0, scale: 0.92, filter: 'blur(12px)' }}
                animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 z-40 flex items-center justify-center px-6"
              >
                <div className="text-center">
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.6 }}
                    className="label-small mb-5"
                    style={{ color: '#C4A35A' }}
                  >
                    {t.intro.credential}
                  </motion.div>

                  <div
                    className="mx-auto mb-6 h-[1px]"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent, #C4A35A, transparent)',
                      width: '160px',
                    }}
                  />

                  <h1
                    className="font-heading font-black metallic-gold leading-[0.92]"
                    style={{
                      fontSize: 'clamp(2.8rem, 8vw, 7rem)',
                      letterSpacing: '-0.02em',
                      WebkitTextStroke: '1px rgba(0,0,0,0.7)',
                    }}
                  >
                    KENNETH CHRISTOPHER
                  </h1>
                  <h2
                    className="font-heading italic leading-[0.95] mt-2"
                    style={{
                      fontSize: 'clamp(3.2rem, 10vw, 9rem)',
                      color: '#C4A35A',
                      textShadow:
                        '0 0 40px rgba(196,163,90,0.45), 0 4px 0 rgba(0,0,0,0.7)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    &ldquo;KC&rdquo; REDDING
                  </h2>

                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.8 }}
                    className="font-heading italic text-xl md:text-2xl mt-8"
                    style={{ color: '#DCCFAD' }}
                  >
                    {t.intro.tagline}
                  </motion.p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Skip */}
          {phase !== 'gate' && (
            <button
              onClick={handleSkip}
              data-testid="intro-skip-button"
              className="absolute bottom-6 right-6 label-small transition-colors z-50"
              style={{ color: '#C4A35A' }}
              aria-label="Skip intro"
            >
              {t.intro.skip} &rarr;
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroSequence;
